/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useContext} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'

import {Box, PopoverTrigger, useTheme} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {mainMenuItemSelected} from '../../integrations/segment/app/page/home'
import {SegmentContext} from '../../integrations/segment/app/contexts'
import {categoryUrlBuilder} from '../../utils/url'

const ListMenuTrigger = ({item, name, isOpen, onOpen, onClose, triggerRef, onBlur, hoverDelay}) => {
    const segmentContext = useContext(SegmentContext)
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const [hoverTimer, setHoverTimer] = useState(null)

    const keyMap = {
        Escape: () => onClose(),
        Enter: () => onOpen()
    }

    const handleMouseOver = () => {
        if (hoverTimer) {
            clearTimeout(hoverTimer)
        }

        const timer = setTimeout(() => {
            onOpen()
        }, hoverDelay)

        setHoverTimer(timer)
    }

    const handleMouseOut = () => {
        // Clear the timer if user leaves before delay completes
        if (hoverTimer) {
            clearTimeout(hoverTimer)
            setHoverTimer(null)
        }
        // You can add onClose() here if you want immediate closing
    }

    useEffect(() => {
        return () => {
            if (hoverTimer) {
                clearTimeout(hoverTimer)
            }
        }
    }, [hoverTimer])

    return (
        <Box {...baseStyle.listMenuTriggerContainer}>
            <PopoverTrigger>
                <Link
                    ref={triggerRef}
                    as={RouteLink}
                    to={categoryUrlBuilder(item)}
                    onClick={() => {
                        mainMenuItemSelected(item, segmentContext)
                    }}
                    onMouseOver={handleMouseOver}
                    onMouseOut={handleMouseOut}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            onOpen()
                        }
                        keyMap[e.key]?.(e)
                    }}
                    onBlur={onBlur}
                    {...baseStyle.listMenuTriggerLink}
                    {...{name: name + ' __'}}
                    {...(isOpen ? baseStyle.listMenuTriggerLinkActive : {})}
                    className={`level-1-link ${name === 'Sale' ? 'saleColor' : ''}`}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    aria-label={
                        name === 'Sale'
                            ? 'View all discounted anime products in our Sale'
                            : undefined
                    }
                    tabIndex="0"
                >
                    {name}
                </Link>
            </PopoverTrigger>
        </Box>
    )
}

ListMenuTrigger.propTypes = {
    item: PropTypes.object,
    name: PropTypes.string,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    menuRef: PropTypes.object,
    triggerRef: PropTypes.object,
    onBlur: PropTypes.func,
    hoverDelay: PropTypes.number
}

export {ListMenuTrigger}
